import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowRight, Plus, Trash2, Wand2 } from 'lucide-react';

import { Stack } from '$layouts/v2/components/Stack.jsx';
import { TemplateInput } from '$layouts/v2/components/TemplateInput.jsx';
import type { FieldInfo } from '$layouts/v2/helpers/context.js';

import { TransitionTargetSelect } from './StepFormPrimitives';

export type StepOption = {
  id: string;
  label: string;
};

export function CasesEditor({
  cases,
  stepIds,
  enumOptions,
  onChange,
  readOnly = false,
  fieldName,
  stepOptions,
  fallbackTarget,
}: {
  cases: Record<string, string>;
  stepIds: string[];
  enumOptions?: string[];
  onChange: (cases: Record<string, string>) => void;
  readOnly?: boolean;
  /** The value being evaluated. Used to make the route condition easier to scan. */
  fieldName?: string;
  /** A human-friendly label for each destination step. */
  stepOptions?: StepOption[];
  /** The fallback destination, when the caller wants it reflected in the coverage summary. */
  fallbackTarget?: string;
}) {
  const entries = Object.entries(cases);
  const knownValues = [...new Set(enumOptions ?? [])];
  const hasCase = (value: string) => Object.keys(cases).includes(value);
  const remainingValues = knownValues.filter((option) => !hasCase(option));

  const valueColumnLabel = fieldName?.trim()
    ? `If ${fieldName.trim()} is…`
    : 'If the value is…';

  const destinationsById = new Map<string, StepOption>();
  for (const option of stepOptions ?? []) {
    destinationsById.set(option.id, option);
  }
  for (const stepId of stepIds) {
    if (!destinationsById.has(stepId)) {
      destinationsById.set(stepId, { id: stepId, label: stepId });
    }
  }
  const destinations = [...destinationsById.values()];
  const destinationLabel = (stepId: string) => {
    const destination = destinationsById.get(stepId);
    if (!destination || destination.label === destination.id) {
      return stepId;
    }
    return `${destination.label} (${destination.id})`;
  };

  const setEntry = (index: number, key: string, target: string) => {
    if (
      key &&
      entries.some(
        ([currentKey], entryIndex) =>
          entryIndex !== index && currentKey === key,
      )
    ) {
      return;
    }

    const next: Record<string, string> = {};
    for (const [entryIndex, [currentKey, currentTarget]] of entries.entries()) {
      if (entryIndex === index) {
        next[key] = target;
      } else {
        next[currentKey] = currentTarget;
      }
    }
    onChange(next);
  };
  const addAll = () => {
    const next = { ...cases };
    for (const option of remainingValues) {
      // A route is incomplete until the user explicitly chooses where it continues.
      next[option] = '';
    }
    onChange(next);
  };
  const addRoute = () => {
    // A record can only represent one unfinished route at a time. Requiring its
    // value first also keeps enum values unique rather than silently overwriting one.
    if (
      hasCase('') ||
      (knownValues.length > 0 && remainingValues.length === 0)
    ) {
      return;
    }
    onChange({ ...cases, '': '' });
  };
  const removeEntry = (key: string) => {
    const next = { ...cases };
    delete next[key];
    onChange(next);
  };

  if (readOnly) {
    return (
      <Box>
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Cases
          </Typography>
        </Stack>
        {entries.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mt: 0.75,
            }}
          >
            No specific routes.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr) 16px minmax(0, 1fr)',
                sm: 'minmax(0, 1fr) 20px minmax(0, 1fr)',
              },
              columnGap: 1,
              rowGap: 0.75,
              alignItems: 'center',
              mt: 0.75,
            }}
          >
            {entries.map(([key, target], index) => (
              <Box key={`${key}-${index}`} sx={{ display: 'contents' }}>
                <Typography
                  variant="body2"
                  sx={{
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {key || '— No value selected —'}
                </Typography>
                <ArrowRight size={14} aria-hidden="true" />
                <Typography
                  variant="body2"
                  sx={{
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {target ? destinationLabel(target) : '— No step selected —'}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        useFlexGap
        sx={{ flexWrap: 'wrap', rowGap: 0.25 }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="baseline"
          sx={{ minWidth: 0 }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Cases
          </Typography>
        </Stack>
        <Stack
          direction="row"
          spacing={0.25}
          alignItems="center"
          sx={{ flexShrink: 0 }}
        >
          {knownValues.length > 0 && remainingValues.length > 0 && (
            <Tooltip
              title={`Add all ${remainingValues.length} remaining ${
                remainingValues.length === 1 ? 'value' : 'values'
              }`}
            >
              <Button
                size="small"
                variant="text"
                sx={{ minWidth: 0, px: 0.75, whiteSpace: 'nowrap' }}
                onClick={addAll}
              >
                Add all ({remainingValues.length})
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Add route">
            <span>
              <IconButton
                size="small"
                aria-label="Add route"
                disabled={
                  hasCase('') ||
                  (knownValues.length > 0 && remainingValues.length === 0)
                }
                onClick={addRoute}
              >
                <Plus size={16} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
      {entries.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr) 16px minmax(0, 1fr) 28px',
              sm: 'minmax(0, 1fr) 20px minmax(0, 1fr) 32px',
            },
            columnGap: 1,
            rowGap: 0.75,
            alignItems: 'center',
            mt: 0.75,
          }}
        >
          {entries.map(([key, target], index) => (
            <Box key={`${key}-${index}`} sx={{ display: 'contents' }}>
              {enumOptions && enumOptions.length > 0 ? (
                <TextField
                  fullWidth
                  size="small"
                  select
                  aria-label={valueColumnLabel}
                  value={key}
                  onChange={(event) =>
                    setEntry(index, event.target.value, target)
                  }
                >
                  <MenuItem value="">— Choose a value —</MenuItem>
                  {[...new Set([...knownValues, ...(key ? [key] : [])])]
                    .filter(
                      (option) =>
                        option === key ||
                        !entries.some(([currentKey]) => currentKey === option),
                    )
                    .map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                </TextField>
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter a value"
                  aria-label={valueColumnLabel}
                  value={key}
                  onChange={(event) =>
                    setEntry(index, event.target.value, target)
                  }
                />
              )}
              <ArrowRight size={14} aria-hidden="true" />
              <TransitionTargetSelect
                stepOptions={destinations}
                value={target}
                onChange={(newTarget) => setEntry(index, key, newTarget)}
                readOnly={readOnly}
              />
              <IconButton
                color="error"
                size="small"
                title="Remove route"
                aria-label="Remove route"
                onClick={() => removeEntry(key)}
              >
                <Trash2 size={14} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export function KeyValueEditor({
  label,
  value,
  availableFields,
  onChange,
  readOnly = false,
}: {
  label: string;
  value: Record<string, string>;
  availableFields: FieldInfo[];
  onChange: (value: Record<string, string>) => void;
  readOnly?: boolean;
}) {
  const entries = Object.entries(value);
  const setEntry = (index: number, key: string, nextValue: string) => {
    const next: Record<string, string> = {};
    for (const [entryIndex, [currentKey, currentValue]] of entries.entries()) {
      if (entryIndex === index) {
        next[key] = nextValue;
      } else {
        next[currentKey] = currentValue;
      }
    }
    onChange(next);
  };
  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2">{label}</Typography>
        {!readOnly && (
          <Button
            size="small"
            startIcon={<Plus size={14} />}
            onClick={() => onChange({ ...value, '': '' })}
          >
            Row
          </Button>
        )}
      </Stack>
      <Stack spacing={1} sx={{ mt: 1 }}>
        {entries.map(([key, currentValue], index) => (
          <Stack direction="row" spacing={1} alignItems="center" key={index}>
            <TextField
              placeholder="field"
              value={key}
              slotProps={{ htmlInput: { readOnly } }}
              onChange={(event) =>
                setEntry(index, event.target.value, currentValue)
              }
            />
            <Wand2 size={12} />
            <Box sx={{ flexGrow: 1 }}>
              <TemplateInput
                value={currentValue}
                fields={availableFields}
                onChange={(nextValue) => setEntry(index, key, nextValue)}
                multiline={false}
                readOnly={readOnly}
              />
            </Box>
            {!readOnly && (
              <IconButton
                color="error"
                size="small"
                title="Remove row"
                onClick={() => {
                  const next = { ...value };
                  delete next[key];
                  onChange(next);
                }}
              >
                <Trash2 size={14} />
              </IconButton>
            )}
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
